import {Injectable} from '@angular/core';
import {MappingEngineGetDetailsResp} from '@em/shared/api-interface';
import {
  AdditionalProcessor,
  BackendAdditionalProcessor,
  BackendCondition,
  BackendConditionLink,
  BackendMappingRule,
  BackendMappingValue,
  CalculationMappingValue,
  Condition,
  ConditionalStatement,
  ConditionalStatementList,
  DestinationAttribute,
  MappingEngine,
  MappingEngineAttributes,
  MappingRule,
  MappingRuleList,
  MappingRuleType,
  MappingValue,
  SimpleMappingValue,
} from '@em/shared/mapping-engine/domain-models';
import {Flatten} from '@em/shared/util-types';

@Injectable({
  providedIn: 'root',
})
export class FrontEndModelBuilderService {
  constructor() {}

  buildDestinationAttributes(
    attributes: MappingEngineAttributes['destination_attributes'],
  ): DestinationAttribute[] {
    return (attributes || []).map((attr) => ({
      attribute: attr.attribute || '',
      required: attr.mandatory,
    }));
  }

  buildMappingEngine(
    apiResponse: MappingEngineGetDetailsResp,
    sourceAttributes: string[],
    destinationAttributes: DestinationAttribute[],
  ): MappingEngine | undefined {
    if (!apiResponse) {
      return undefined;
    }

    return {
      isDefault: apiResponse.is_default,
      mappingRuleList: this.buildMappingRuleListArray(
        apiResponse.attributes,
        sourceAttributes,
        destinationAttributes,
      ),
    };
  }

  buildMappingRuleListArray(
    backendAttributesArray: MappingEngineGetDetailsResp['attributes'],
    sourceAttributes: string[],
    destinationAttributes: DestinationAttribute[],
  ): MappingRuleList[] {
    // filter list if single rule and attribute is not in source list
    const filteredRules = this._filterInvalidRules(
      backendAttributesArray,
      sourceAttributes,
    ).sort(this._getRuleComparer(sourceAttributes, destinationAttributes));
    this._sortByOrder(filteredRules);

    return filteredRules.map((backendAttribute) =>
      this.buildMappingRuleList(backendAttribute),
    );
  }

  buildMappingRuleList(
    mappingRuleList: Flatten<MappingEngineGetDetailsResp['attributes']>,
  ): MappingRuleList {
    return {
      attribute: mappingRuleList.attribute,
      stripHTML: mappingRuleList.strip_html,
      custom: mappingRuleList.custom,
      mappingRules: mappingRuleList.rules
        .sort((a, b) => a.order - b.order)
        .map((rule) => this.buildMappingRule(rule)),
    };
  }

  buildMappingRule(mappingRule: BackendMappingRule): MappingRule {
    return {
      mappingType: mappingRule.map_to,
      mappingValue: this.buildMappingValue(
        mappingRule.map_to_value,
        mappingRule.map_to,
      ),
      additionalProcessor: this.buildAdditionalProcessor(
        mappingRule.additional_processor,
      ),
      conditionalStatementList: mappingRule.conditions?.length
        ? this.buildConditions(mappingRule.conditions, mappingRule.links)
        : undefined,
    };
  }

  buildMappingValue(
    mappingValue: BackendMappingValue,
    mappingType: MappingRuleType,
  ): MappingValue {
    if (!mappingValue) {
      return null;
    } else if (
      mappingType === 'combine_attributes' &&
      Array.isArray(mappingValue)
    ) {
      return mappingValue.map((mp) => ({
        type: mp.type,
        value: mp.value,
      }));
    } else if (mappingType === 'calculation') {
      const mp = mappingValue as CalculationMappingValue;
      return {
        attribute_name: mp.attribute_name,
        operator: mp.operator,
        value: mp.value,
      };
    } else {
      const mp = mappingValue as SimpleMappingValue;
      return {type: mp.type, value: mp.value};
    }
  }

  buildAdditionalProcessor(
    backend_processor: BackendAdditionalProcessor,
  ): AdditionalProcessor {
    const finalProcessor: AdditionalProcessor = {};

    for (const key in backend_processor) {
      if (Object.prototype.hasOwnProperty.call(backend_processor, key)) {
        const typedKey = key as keyof AdditionalProcessor;
        const element = backend_processor[typedKey];
        finalProcessor[typedKey] = element;
      }
    }

    return finalProcessor;
  }

  buildConditions(
    conditions: BackendCondition[] | undefined,
    links: BackendConditionLink[] | undefined,
  ): ConditionalStatementList {
    const groupLink: BackendConditionLink = links?.find(
      (link) => !link?.parent_uuid,
    );

    const conditionalStatements: ConditionalStatement[] = [];

    links
      ?.filter((link) => !!link?.parent_uuid)
      .forEach((link) => {
        const linkConditions = conditions?.filter(
          (condition) => condition?.parent_uuid === link?.parent_uuid,
        );

        conditionalStatements.push({
          logicalOperator: link?.operator || null,
          conditions:
            linkConditions?.map((condition) =>
              this.buildCondition(condition),
            ) || [],
        });
      });

    const allLinksUUid = links
      ?.filter((link) => !!link?.parent_uuid)
      .map((link) => link?.parent_uuid);

    // Conditions without links, in case of only single condition
    conditions
      ?.filter((condition) => !allLinksUUid?.includes(condition?.parent_uuid))
      .forEach((condition) => {
        conditionalStatements.push({
          logicalOperator: null,
          conditions: [this.buildCondition(condition)],
        });
      });

    return {
      conditionalStatements,
      logicalOperator: groupLink?.operator || null,
    };
  }

  buildCondition(backendCondition: BackendCondition): Condition {
    return {
      leftOperand: {type: 'attribute', value: backendCondition?.attribute},
      operator: backendCondition?.compare_operator,
      rightOperand: {
        value: backendCondition?.compare_value?.value,
        type: backendCondition?.compare_value?.type || 'static_value',
      },
    };
  }

  private _filterInvalidRules(
    rulesLists: MappingEngineGetDetailsResp['attributes'],
    sourceAttributes: string[],
  ): MappingEngineGetDetailsResp['attributes'] {
    return rulesLists.filter((rulesList) => {
      if (rulesList.rules.length === 1) {
        const theOnlyRule = rulesList.rules[0];
        // don't reutrn rulesList of single rule with attribute no exist in the sourceAttributes list
        if (
          theOnlyRule.map_to === 'attribute' &&
          theOnlyRule.map_to_value?.type === 'attribute' &&
          sourceAttributes.indexOf(theOnlyRule.map_to_value.value) <= -1
        ) {
          return false;
        }
      }

      return true;
    });
  }

  private _sortByOrder(
    rulesLists: MappingEngineGetDetailsResp['attributes'],
  ): MappingEngineGetDetailsResp['attributes'] {
    return rulesLists.sort((a, b) => (a.order || 0) - (b.order || 0));
  }

  private _getRuleComparer(
    sourceAttributes: string[],
    destinationAttributes: DestinationAttribute[],
  ): (a: {attribute: string}, b: {attribute: string}) => number {
    return (a: {attribute: string}, b: {attribute: string}): number => {
      const destinationSortList = destinationAttributes
        .filter((at) => at.required)
        .map((at) => at.attribute);

      if (!destinationSortList.length) {
        return 0;
      }

      const A1 = destinationSortList.indexOf(a.attribute);
      const B1 = destinationSortList.indexOf(b.attribute);
      const A2 = sourceAttributes.indexOf(a.attribute);
      const B2 = sourceAttributes.indexOf(b.attribute);

      if (A1 > -1 && B1 > -1) {
        return A2 - B2 > 0 ? 1 : -1;
      } else if (A1 > -1) {
        return -1;
      } else if (B1 > -1) {
        return 1;
      } else {
        return A2 - B2 > 0 ? 1 : -1;
      }
    };
  }
}
